import {
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Box, useTheme } from "@mui/material";
import { HotTable } from "@handsontable/react";
import { registerAllModules } from "handsontable/registry";
import type { TextRow, AreaRow } from "./types";
import { validateTextRow, validateAreaRow } from "./validation";
import { createTextErrorRenderer, createColorRenderer } from "./renderers";
import { buildTableStyles } from "./styles";
import { ColorPickerPopover } from "./colorPicker";

registerAllModules();

export interface ShapeElementsTableHandle {
  // Lê os dados atuais diretamente da instância do Handsontable.
  getRows: () => (TextRow | AreaRow)[];
}

type ShapeKind = "text" | "area";

export interface ShapeElementsTableProps {
  kind: ShapeKind;
  data: (TextRow | AreaRow)[];
  onDataChange: (rows: (TextRow | AreaRow)[]) => void;
}

// Propriedades de cor por kind (colunas que abrem o color picker).
const COLOR_PROPS: Record<ShapeKind, string[]> = {
  text: ["textColor", "backgroundColor"],
  area: ["color"],
};

// Revalida uma linha de acordo com o kind.
function validateByKind(kind: ShapeKind, row: any): Record<string, string> {
  return kind === "text"
    ? validateTextRow(row as TextRow)
    : validateAreaRow(row as AreaRow);
}

// Tabela de edição em massa para os tipos "Texto" (tipo 3) e "Área" (tipo 4).
// Reaproveita a mesma estrutura do Handsontable de ElementsTable, mas com
// colunas próprias e um popover de cor (no lugar dos popovers de ícone/tags).
const ShapeElementsTable = forwardRef<
  ShapeElementsTableHandle,
  ShapeElementsTableProps
>(function ShapeElementsTable({ kind, data, onDataChange }, ref) {
  const theme = useTheme();
  const hotRef = useRef<any>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [tableHeight, setTableHeight] = useState(0);

  useImperativeHandle(ref, () => ({
    getRows: () =>
      (hotRef.current?.hotInstance?.getSourceData() as (TextRow | AreaRow)[]) ||
      [],
  }));

  // Mede o container e mantém a altura do grid sincronizada (responsivo).
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const update = () => setTableHeight(el.clientHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Refs para acesso estável dentro de callbacks com deps vazias
  const kindRef = useRef(kind);
  useEffect(() => {
    kindRef.current = kind;
  }, [kind]);

  const onDataChangeRef = useRef(onDataChange);
  useEffect(() => {
    onDataChangeRef.current = onDataChange;
  }, [onDataChange]);

  // Popover de seleção de cor
  const [colorPopover, setColorPopover] = useState<{
    anchorEl: HTMLElement | null;
    row: number;
    prop: string;
  } | null>(null);

  // Revalida todas as linhas e emite nova referência → re-render.
  const emitRevalidated = useCallback(() => {
    const hot = hotRef.current?.hotInstance;
    if (!hot) return;
    const allData = hot.getSourceData() as any[];
    allData.forEach((row) => {
      row._errors = validateByKind(kindRef.current, row);
    });
    onDataChangeRef.current([...allData]);
  }, []);

  // Callback após qualquer alteração de célula.
  const handleAfterChange = useCallback(
    (changes: any[] | null, source: string) => {
      if (!changes || source === "loadData" || source === "internal_update")
        return;
      const hot = hotRef.current?.hotInstance;
      if (!hot) return;

      // Ao mudar o modo (text/html) as colunas de cor mudam de estado
      // (habilitadas só no modo texto) → força um re-render do grid.
      const modeChanged = changes.some(
        ([, prop, oldV, newV]: [number, string, any, any]) =>
          prop === "mode" && oldV !== newV,
      );
      if (modeChanged && !hot.isDestroyed) hot.render();

      emitRevalidated();
    },
    [emitRevalidated],
  );

  // Alterna a seleção de todas as linhas (exclusão em massa na edição).
  const handleToggleSelectAll = useCallback(() => {
    const hot = hotRef.current?.hotInstance;
    if (!hot) return;
    const allData = hot.getSourceData() as any[];
    const allChecked =
      allData.length > 0 && allData.every((row) => row.checked);
    allData.forEach((row) => {
      row.checked = !allChecked;
    });
    onDataChangeRef.current([...allData]);
  }, []);

  // Renderiza e sincroniza o checkbox "selecionar todos" no cabeçalho.
  const handleAfterGetColHeader = useCallback(
    (col: number, TH: HTMLElement) => {
      if (col !== 0 || !TH) return;
      const span =
        (TH.querySelector(".colHeader") as HTMLElement | null) || TH;
      let checkbox = span.querySelector(
        "input.ht-select-all",
      ) as HTMLInputElement | null;
      if (!checkbox) {
        span.textContent = "";
        checkbox = document.createElement("input");
        checkbox.type = "checkbox";
        checkbox.className = "ht-select-all";
        span.appendChild(checkbox);
      }
      const hot = hotRef.current?.hotInstance;
      const allData = (hot?.getSourceData() as any[]) || [];
      const total = allData.length;
      const checked = allData.filter((r) => r.checked).length;
      checkbox.checked = total > 0 && checked === total;
      checkbox.indeterminate = checked > 0 && checked < total;
    },
    [],
  );

  // Intercepta cliques nas células de cor para abrir o color picker (mesmo
  // padrão do popover de ícone de ElementsTable). Células desabilitadas
  // (modo html) são ignoradas. Também trata o clique no cabeçalho da coluna 0
  // (selecionar todos).
  const handleBeforeOnCellMouseDown = useCallback(
    (event: any, coords: any, TD: any) => {
      const hotInstance = hotRef.current?.hotInstance;
      if (!hotInstance) return;

      // Clique no cabeçalho da coluna 0 → alterna seleção.
      if (coords.row === -1) {
        if (coords.col === 0) {
          event.preventDefault();
          event.stopImmediatePropagation();
          handleToggleSelectAll();
        }
        return;
      }
      if (coords.row < 0) return;

      const prop = hotInstance.colToProp(coords.col) as string;
      if (!COLOR_PROPS[kindRef.current].includes(prop)) return;

      const rowData = hotInstance.getSourceDataAtRow(coords.row) as any;
      if (kindRef.current === "text" && rowData?.mode === "html") return;

      const sel = hotInstance.getSelectedRangeLast();
      const alreadySelected =
        sel &&
        coords.row >= sel.from.row &&
        coords.row <= sel.to.row &&
        coords.col >= sel.from.col &&
        coords.col <= sel.to.col;
      if (!alreadySelected) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      setColorPopover({ anchorEl: TD, row: coords.row, prop });
    },
    [handleToggleSelectAll],
  );

  //  Renderers e configuração das colunas
  const textErrorRenderer = useMemo(() => createTextErrorRenderer(), []);
  const colorRenderer = useMemo(() => createColorRenderer(), []);

  const columns = useMemo(() => {
    // Coluna de checkbox (posição 0) para exclusão em massa na edição.
    const checkboxCol = {
      data: "checked",
      title: "",
      type: "checkbox",
      width: 22,
      className: "htCenter htMiddle",
    };
    if (kind === "text") {
      return [
        checkboxCol,
        {
          data: "mode",
          title: "Modo",
          type: "dropdown",
          source: ["text", "html"],
          width: 40,
          renderer: textErrorRenderer,
        },
        {
          data: "textColor",
          title: "Cor do texto",
          width: 40,
          editor: false,
          renderer: colorRenderer,
        },
        {
          data: "backgroundColor",
          title: "Cor de fundo",
          width: 40,
          editor: false,
          renderer: colorRenderer,
        },
        {
          data: "content",
          title: "Conteúdo",
          type: "text",
          width: 300,
          renderer: textErrorRenderer,
        },
        {
          data: "lat",
          title: "Latitude",
          type: "text",
          width: 70,
          allowInvalid: true,
          renderer: textErrorRenderer,
        },
        {
          data: "long",
          title: "Longitude",
          type: "text",
          width: 70,
          allowInvalid: true,
          renderer: textErrorRenderer,
        },
      ];
    }
    return [
      checkboxCol,
      {
        data: "titulo",
        title: "Título",
        type: "text",
        width: 220,
        renderer: textErrorRenderer,
      },
      {
        data: "color",
        title: "Cor",
        width: 20,
        editor: false,
        renderer: colorRenderer,
      },
    ];
  }, [kind, textErrorRenderer, colorRenderer]);

  // No modo "html" as colunas de cor não se aplicam → readOnly (bloqueia
  // edição, colagem e autofill nessas células).
  const cellsConfig = useCallback(
    (row: number, _col: number, prop: string | number) => {
      const cellProperties: any = {};
      if (kindRef.current !== "text") return cellProperties;
      if (prop !== "textColor" && prop !== "backgroundColor")
        return cellProperties;
      const hotInstance = hotRef.current?.hotInstance;
      const rowData = hotInstance?.getSourceDataAtRow(row) as TextRow;
      if (rowData?.mode === "html") cellProperties.readOnly = true;
      return cellProperties;
    },
    [],
  );

  const customStyles = useMemo(() => buildTableStyles(theme), [theme]);

  useEffect(() => {
    const hot = hotRef.current?.hotInstance;
    if (!hot) return;
    const rafId = requestAnimationFrame(() => {
      if (!hot.isDestroyed) hot.render();
    });
    return () => cancelAnimationFrame(rafId);
  }, []);

  const currentColorValue =
    colorPopover && hotRef.current?.hotInstance
      ? ((
          hotRef.current.hotInstance.getSourceDataAtRow(colorPopover.row) as any
        )?.[colorPopover.prop] ?? "")
      : "";

  return (
    <>
      <style>{customStyles}</style>
      <Box
        ref={wrapperRef}
        className="custom-table-wrapper"
        sx={{ height: "100%", overflow: "hidden", width: "100%" }}
      >
        <HotTable
          ref={hotRef}
          data={data}
          licenseKey="non-commercial-and-evaluation"
          rowHeaders={false}
          width="100%"
          height={tableHeight || undefined}
          stretchH="all"
          cells={cellsConfig}
          columns={columns}
          afterChange={handleAfterChange}
          beforeOnCellMouseDown={handleBeforeOnCellMouseDown}
          afterGetColHeader={handleAfterGetColHeader}
          manualColumnResize={true}
          rowHeights={32}
          className="custom-table-wrapper"
        />
      </Box>

      <ColorPickerPopover
        open={Boolean(colorPopover)}
        anchorEl={colorPopover?.anchorEl ?? null}
        value={currentColorValue}
        onClose={() => setColorPopover(null)}
        onChange={(next) => {
          const hot = hotRef.current?.hotInstance;
          if (hot && colorPopover) {
            hot.setDataAtRowProp(
              colorPopover.row,
              colorPopover.prop,
              next,
              "colorPicker",
            );
          }
        }}
      />
    </>
  );
});

export default ShapeElementsTable;
